import { i18n } from '@lingui/core'
import { Trans } from '@lingui/react'
import { isNil } from 'ramda'

import type { Range } from '@twisto/api-graphql'
import { Box } from '@twisto/components/atoms/box'
import { TextButton } from '@twisto/components/atoms/button'
import type { CastedSubmitHandler } from '@twisto/components/atoms/form'
import { Form, useForm } from '@twisto/components/atoms/form'
import { FormattedCurrencyNumber } from '@twisto/components/atoms/format'
import { Slider } from '@twisto/components/atoms/slider'
import { Typography } from '@twisto/components/atoms/typography'
import { InputAmount } from '@twisto/components/molecules/input-amount/input-amount'
import { BigMoneyCrossedIcon } from '@twisto/icons'

import * as styles from './select-cash-amount-step-form.styles'

const fieldNames = {
  amount: 'amount',
} as const

export type FormValues = {
  [fieldNames.amount]: number
}

type Props = {
  cashOfferRange?: Range | null
  onSubmit: CastedSubmitHandler<FormValues>
  loading: boolean
}

export const SelectCashAmountStepForm = ({
  cashOfferRange,
  onSubmit,
  loading,
}: Props) => {
  const min = Number(cashOfferRange?.min ?? 0)
  const max = Number(cashOfferRange?.max ?? 0)

  const formContext = useForm<FormValues>({
    defaultValues: {
      amount: min,
    },
  })

  return (
    <Form {...formContext} onSubmit={onSubmit}>
      <InputAmount
        ariaLabel={
          /*i18n*/
          i18n._('flow.selectCashAmountStep.inputAmount.ariaLabel')
        }
        disabled={isNil(cashOfferRange)}
        max={max}
        min={min}
        name={fieldNames.amount}
      />

      <div css={styles.slider}>
        {!isNil(cashOfferRange) ? (
          <Slider
            showLabel
            labelFormatter={(value) => (
              <FormattedCurrencyNumber
                minimumFractionDigits={0}
                value={value}
              />
            )}
            max={max}
            min={min}
            name={fieldNames.amount}
          />
        ) : (
          <Box
            color="warning"
            startAdornment={
              <BigMoneyCrossedIcon color="warning" css={styles.icon} />
            }
          >
            <Typography variant="b2">
              <Trans id="flow.selectCashAmountStep.cashOfferRange.warning" />
            </Typography>
          </Box>
        )}
      </div>

      <TextButton
        fullWidth
        color="primary"
        disabled={isNil(cashOfferRange) || loading}
        type="submit"
      >
        <Trans id="flow.selectCashAmountStep.submitButton" />
      </TextButton>
    </Form>
  )
}
